import React, { useState } from 'react';
import { Popconfirm, Switch } from 'antd';
import { observer } from 'mobx-react';
import { useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { theme } from 'antd';
import { permissions } from '@/constants';
import { useStores } from '@/hooks/use-stores';
import { isEcoplantAdmin } from '@/utils/util-functions';
import { CustomThemeToken } from '@/theme';

import ToggleContainer from './ToggleContainer';
import SpinCentered from '@/comp/SpinCentered';

const { useToken } = theme;

export const MaintenanceSwitch = observer(
  ({
    inMaintenance,
    deviceId,
    stopPropagation = true,
    isBeta = false,
  }: {
    inMaintenance: boolean;
    deviceId: string;
    stopPropagation?: boolean;
    isBeta?: boolean;
  }) => {
    const { controlCenterStore, permissionStore, stationStore, userStore } = useStores();
    const { stationId } = useParams();
    const { t } = useTranslation();
    const [loading, setLoading] = useState(false);
    const isAdmin = isEcoplantAdmin(userStore, stationStore, stationId);
    const { token } = useToken() as unknown as CustomThemeToken;
    const disabled = isBeta ? !isAdmin : loading || !permissionStore.isAuth(permissions.UPDATE_DEVICE_MAINTENANCE);
    const [maintenanceMessage, setMaintenanceMessage] = useState('');

    const onChangeSwitch = async value => {
      try {
        setLoading(true);
        await controlCenterStore.setMaintenance(deviceId, value);
      } finally {
        setLoading(false);
        setMaintenanceMessage('');
      }
    };

    const onClickWrapper = async e => {
      stopPropagation && e.stopPropagation();
      setLoading(true);
      const maintenanceMessage = await controlCenterStore.fetchMaintenanceMessage(deviceId, inMaintenance);
      setMaintenanceMessage(maintenanceMessage);
      setLoading(false);
    };

    return (
      <ToggleContainer
        $switchState={inMaintenance}
        onClick={onClickWrapper}
        $colorOn={token.colorBGMaintenance}
        $colorOff={token.colorBorderSecondary}
        $toggleColorOn={token.colorWarning}
        $toggleColorOff={token.colorBorder}
        $isDisabled={disabled}
      >
        <div className="switch-title">{t('Maintenance')}</div>
        <Popconfirm
          placement="top"
          title={loading ? <SpinCentered color={token.colorPrimary} className="spinner" /> : maintenanceMessage}
          onConfirm={() => onChangeSwitch(!inMaintenance)}
          okText={t('Yes')}
          cancelText={t('Cancel')}
          icon={null}
          disabled={disabled}
        >
          <div className="switch-options">
            {inMaintenance ? (
              <span className="switch-value-text">{t('Yes')}</span>
            ) : (
              <span className="switch-value-text">{t('No')}</span>
            )}
            <Switch size="default" checked={inMaintenance} loading={loading} disabled={disabled} />
          </div>
        </Popconfirm>
      </ToggleContainer>
    );
  },
);
